import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import CheckBox from "../Components/CheckBox";
import DateOption1 from "../Components/DateOption1";
import { default as RadioOption } from "../Components/RadioOption";
import Layout from "../Layout/Layout";
import AlarmApply from "../Modals/AlarmApply";
import AlarmCancel from "../Modals/AlarmCancel";
import AlarmInputCheck from "../Modals/AlarmInputCheck";
import "../Styles/component.css";
import "../Styles/global.css";
import "../Styles/lists.css";
import { postJson } from "../Utils/apiUtils";

const DAYS = ["월", "화", "수", "목", "금", "토", "일"];
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, index) =>
  String(index).padStart(2, "0")
);
const MINUTE_OPTIONS = Array.from({ length: 12 }, (_, index) =>
  String(index * 5).padStart(2, "0")
);

const LEGACY_RESERVE_TIME_MAP = {
  1: { reserveHour: 20, reserveMinute: 0 },
  2: { reserveHour: 12, reserveMinute: 50 },
};

const parseReserveValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsedValue = Number(value);
  return Number.isNaN(parsedValue) ? null : parsedValue;
};

const formatReserveValue = (value) => {
  const parsedValue = parseReserveValue(value);
  return parsedValue === null ? "" : String(parsedValue).padStart(2, "0");
};

const getReserveTimeFromDetail = ({ reserveHour, reserveMinute, timeType }) => {
  const parsedHour = parseReserveValue(reserveHour);
  const parsedMinute = parseReserveValue(reserveMinute);

  if (parsedHour !== null && parsedMinute !== null) {
    return {
      reserveHour: formatReserveValue(parsedHour),
      reserveMinute: formatReserveValue(parsedMinute),
    };
  }

  const legacyReserveTime = LEGACY_RESERVE_TIME_MAP[timeType];

  return {
    reserveHour: legacyReserveTime
      ? formatReserveValue(legacyReserveTime.reserveHour)
      : "",
    reserveMinute: legacyReserveTime
      ? formatReserveValue(legacyReserveTime.reserveMinute)
      : "",
  };
};

const getLegacyTimeTypeFromReserveTime = (reserveHour, reserveMinute) => {
  const parsedHour = parseReserveValue(reserveHour);
  const parsedMinute = parseReserveValue(reserveMinute);

  if (parsedHour === 20 && parsedMinute === 0) {
    return 1;
  }

  if (parsedHour === 12 && parsedMinute === 50) {
    return 2;
  }

  return null;
};

const parseWeekType = (weekType = "") =>
  weekType
    .split(",")
    .map((day) => Number(day))
    .filter((day) => Number.isInteger(day) && day >= 1 && day <= 7);

const isValidReserveTimeSelection = (reserveHour, reserveMinute) => {
  const parsedHour = parseReserveValue(reserveHour);
  const parsedMinute = parseReserveValue(reserveMinute);

  if (parsedHour === null || parsedMinute === null) {
    return false;
  }

  return parsedMinute >= 0 && parsedMinute <= 55 && parsedMinute % 5 === 0;
};

const PushApplyContent = () => {
  const { id } = useParams();
  const isEditing = !!id;
  const navigate = useNavigate();

  const [nowOrLater, setNowOrLater] = useState(0);
  const [nation, setNation] = useState(0);

  const [reserveHour, setReserveHour] = useState("");
  const [reserveMinute, setReserveMinute] = useState("");
  const [dayOptions, setDayOptions] = useState([]);
  const [startTime, setStartTime] = useState(new Date());
  const [endTime, setEndTime] = useState(new Date());

  const [content_count, setContent_count] = useState(0);
  const [content, setContent] = useState("");
  const lastPushDetailRequestKey = useRef(null);

  const [alarmState, setAlarmState] = useState({ show: false, type: "" });
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);

  const MAX_CONTENT_LENGTH = 170;
  const handleContentChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_CONTENT_LENGTH) {
      setContent(value);
      setContent_count(value.length);
    }
  };

  const handleApply = () => {
    if (!isButtonEnabled) {
      setAlarmState({ show: true, type: "input_check" });
    } else {
      setAlarmState({ show: true, type: "apply" });
    }
  };

  const handleCancel = () => {
    setAlarmState({ show: true, type: "cancel" });
  };

  const getPushDetail = async () => {
    try {
      const res = await postJson(
        "/dashboard/push/getDetail",
        { pushHistoryId: id },
        navigate
      );

      if (res.code === 200) {
        setNowOrLater(res.data.isReserve);
        setNation(
          res.data.cCode === "kr" ? 1 : res.data.cCode === "00" ? 0 : 2
        );
        const reserveTime = getReserveTimeFromDetail(res.data);

        setReserveHour(reserveTime.reserveHour);
        setReserveMinute(reserveTime.reserveMinute);
        setDayOptions(parseWeekType(res.data.weekType));

        if (res.data.startTime) {
          setStartTime(new Date(res.data.startTime));
        }

        if (res.data.endTime) {
          setEndTime(new Date(res.data.endTime));
        }

        setContent(res.data.content || "");
      }
    } catch (error) {
      console.error("푸시 상세 가져오기 중 에러:", error);
    }
  };

  const buildPushBody = () => {
    const parsedReserveHour = parseReserveValue(reserveHour);
    const parsedReserveMinute = parseReserveValue(reserveMinute);

    return {
      isReserve: nowOrLater,
      cCode: nation === 1 ? "kr" : nation === 2 ? "99" : "00",
      timeType:
        nowOrLater === 1
          ? getLegacyTimeTypeFromReserveTime(
              parsedReserveHour,
              parsedReserveMinute
            )
          : null,
      reserveHour: nowOrLater === 1 ? parsedReserveHour : null,
      reserveMinute: nowOrLater === 1 ? parsedReserveMinute : null,
      weekType: dayOptions.join(","),
      startTime,
      endTime,
      content,
    };
  };

  const createPush = async () => {
    try {
      const body = buildPushBody();

      const res = await postJson("/dashboard/push/create", body, navigate);

      if (res.code === 200) {
        navigate("/push", {
          state: { toast_enable: true, type: "success" },
        });
      }
    } catch (error) {
      console.error("푸시알림 등록 중 에러:", error);
    }
  };

  const updatePush = async () => {
    try {
      const body = {
        pushHistoryId: id,
        ...buildPushBody(),
      };

      const res = await postJson("/dashboard/push/update", body, navigate);
      if (res.code === 200) {
        navigate("/push", {
          state: { toast_enable: true, type: "success" },
        });
      }
    } catch (error) {
      console.error("푸시알림 수정 중 에러:", error);
    }
  };

  useEffect(() => {
    setContent_count(content.length);
  }, [content]);

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    const requestKey = JSON.stringify({ id });

    if (lastPushDetailRequestKey.current === requestKey) {
      return;
    }

    lastPushDetailRequestKey.current = requestKey;
    getPushDetail();
  }, [id, isEditing]);

  useEffect(() => {
    // 기본 필수 항목: 푸시 국가(nation), 내용(content)
    // nation은 0(전체), 1(국내), 2(해외) 중 하나여야 함
    const hasNation = nation === 0 || nation === 1 || nation === 2;
    const hasContent = content.trim().length > 0;
    const hasBasicRequired = hasNation && hasContent;

    // 즉시발송인 경우: 기본 필수 항목만 체크
    if (nowOrLater === 0) {
      setIsButtonEnabled(hasBasicRequired);
      return;
    }

    // 예약발송인 경우: 기본 필수 항목 + 예약 관련 필수 항목
    if (nowOrLater === 1) {
      const hasReserveTime = isValidReserveTimeSelection(
        reserveHour,
        reserveMinute
      );
      const hasDayOptions = Array.isArray(dayOptions) && dayOptions.length > 0;
      const hasPeriod = startTime && endTime;

      setIsButtonEnabled(
        hasBasicRequired &&
          hasReserveTime &&
          hasDayOptions &&
          hasPeriod
      );
      return;
    }

    // 초기 상태일 때는 false
    setIsButtonEnabled(false);
  }, [
    nowOrLater,
    reserveHour,
    reserveMinute,
    nation,
    dayOptions,
    startTime,
    endTime,
    content,
  ]);

  return (
    <div>
      {alarmState.show &&
        (alarmState.type === "input_check" ? (
          <AlarmInputCheck
            handleClickX={() => setAlarmState({ show: false, type: "" })}
          />
        ) : alarmState.type === "apply" ? (
          <AlarmApply
            title="새로운 푸시 알림을 등록하시겠습니까?"
            subtitle="등록 시 랭킹마라톤 앱에 데이터가 반영됩니다."
            handleClickX={() => setAlarmState({ show: false, type: "" })}
            handleApply={isEditing ? updatePush : createPush}
          />
        ) : (
          <AlarmCancel
            title="작성하던 내용을 취소하시겠습니까?"
            subtitle="작성하던 내용은 저장되지 않습니다."
            handleCancel={() => navigate("/push")}
            handleClickX={() => setAlarmState({ show: false, type: "" })}
          />
        ))}
      <PushApplyBox>
        <div className="title-in-box">
          푸시알림 {isEditing ? "수정" : "등록"}
        </div>
        <div className="hrz-wrapper">
          <div
            className="text-for-notice"
            style={{ marginLeft: "auto", marginTop: "10px" }}
          >
            *표시는 필수 입력 항목입니다.
          </div>
        </div>

        <div
          className="bold-text"
          style={{ marginTop: "30px", marginBottom: "8px", fontSize: "18px" }}
        >
          옵션 선택
        </div>
        <RadioOption
          title="발송 옵션*"
          noticeMessage=""
          option1="즉시발송"
          option2="예약발송"
          optionChange={setNowOrLater}
          initialOption={nowOrLater}
          component={
            <Reservation
              nowOrLater={nowOrLater}
              dayOptions={dayOptions}
              setDayOptions={setDayOptions}
              reserveHour={reserveHour}
              setReserveHour={setReserveHour}
              reserveMinute={reserveMinute}
              setReserveMinute={setReserveMinute}
              startTime={startTime}
              setStartTime={setStartTime}
              endTime={endTime}
              setEndTime={setEndTime}
            />
          }
        />

        <RadioOption
          title="푸시 국가*"
          noticeMessage=""
          option1="전체"
          option2="국내"
          option3="해외"
          initialOption={nation}
          optionChange={setNation}
        />

        <div className="hrz-wrapper" style={{ marginTop: "40px" }}>
          <div className="subtitle-in-box">내용 작성*</div>
        </div>

        <div
          className="textinputBox"
          style={{ marginTop: "14px", flexDirection: "column" }}
        >
          <textarea
            type="text"
            placeholder="내용을 입력해주세요"
            style={{ width: "700px", height: "176px", resize: "none" }}
            className="textinput"
            value={content}
            onChange={handleContentChange}
            maxLength={MAX_CONTENT_LENGTH}
          />
          <div style={{ color: "#888888", marginLeft: "auto" }}>
            {content_count}/{MAX_CONTENT_LENGTH}
          </div>
        </div>

        <div style={{ display: "flex", marginTop: "30px" }}>
          <div
            className="cancelBtn center"
            style={{ marginLeft: "auto" }}
            onClick={handleCancel}
          >
            취소
          </div>
          <div
            className="applyBtn center"
            style={{ marginLeft: "16px" }}
            onClick={handleApply}
          >
            등록
          </div>
        </div>
      </PushApplyBox>
    </div>
  );
};

const Reservation = ({
  nowOrLater,
  dayOptions,
  setDayOptions,
  reserveHour,
  setReserveHour,
  reserveMinute,
  setReserveMinute,
  startTime,
  endTime,
  setStartTime,
  setEndTime,
}) => {
  return (
    <div>
      {nowOrLater === 1 && (
        <div style={{ marginTop: "30px" }}>
          <p className="medium-text" style={{ marginBottom: "14px" }}>
            시간*
          </p>
          <TimeSelectRow>
            <TimeSelectBox>
              <TimeSelect
                aria-label="예약 시간 시"
                value={reserveHour}
                onChange={(e) => setReserveHour(e.target.value)}
              >
                <option value="">시</option>
                {HOUR_OPTIONS.map((hour) => (
                  <option key={hour} value={hour}>
                    {hour}
                  </option>
                ))}
              </TimeSelect>
              <TimeSelectArrow />
            </TimeSelectBox>
            <TimeDivider>:</TimeDivider>
            <TimeSelectBox>
              <TimeSelect
                aria-label="예약 시간 분"
                value={reserveMinute}
                onChange={(e) => setReserveMinute(e.target.value)}
              >
                <option value="">분</option>
                {MINUTE_OPTIONS.map((minute) => (
                  <option key={minute} value={minute}>
                    {minute}
                  </option>
                ))}
              </TimeSelect>
              <TimeSelectArrow />
            </TimeSelectBox>
          </TimeSelectRow>
          <div style={{ height: "30px" }} />
          <CheckBox
            title="요일*"
            options={DAYS}
            initialOption={dayOptions
              .map((day) => DAYS[day - 1])
              .filter(Boolean)}
            onChange={setDayOptions}
          />

          <p className="medium-text" style={{ marginTop: "30px" }}>
            푸시 기간*
          </p>
          <div style={{ height: "14px" }} />
          <DateOption1
            startTitle="시작일"
            endTitle="종료일"
            width="290px"
            gap="20px"
            startDate={startTime}
            endDate={endTime}
            setStartDate={setStartTime}
            setEndDate={setEndTime}
          />
        </div>
      )}
    </div>
  );
};

const TimeSelectRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TimeSelectBox = styled.div`
  position: relative;
  width: 120px;
`;

const TimeSelect = styled.select`
  width: 100%;
  height: 50px;
  border: 1px solid #d9d9d9;
  border-radius: 12px;
  padding: 0 44px 0 18px;
  background-color: #ffffff;
  color: ${(props) => (props.value === "" ? "#888888" : "#000000")};
  font-size: 18px;
  outline: none;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  &:focus {
    border-color: #1167ed;
  }
`;

const TimeSelectArrow = styled.div`
  position: absolute;
  top: 50%;
  right: 18px;
  width: 10px;
  height: 10px;
  border-right: 2px solid #888888;
  border-bottom: 2px solid #888888;
  transform: translateY(-65%) rotate(45deg);
  pointer-events: none;
`;

const TimeDivider = styled.div`
  font-size: 24px;
  color: #222222;
`;

const PushApplyBox = styled.div`
  width: auto;
  height: auto;
  border-radius: 20px;
  margin-left: 413px;
  margin-top: 30px;
  margin-bottom: 30px;
  padding: 30px;
  background-color: #ffffff;
`;

const PushApply = () => {
  return <Layout page={<PushApplyContent />} />;
};

export default PushApply;
